import { InquiryStatusEnum, KYBProgressStatusEnum } from 'anchor-kyb-service/types';
import { useOverlayContext } from 'seagull';
import { useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { usePersonaKYBStatus } from '~/hooks/reactQuery/queries/usePersonaKYBStatus';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { KycCompletionStatus } from '~/store/business-types';
import { selectors } from '~/store/state-selectors';
const personaInquiryLoader = 'persona-inquiry';
export function shouldStartPersonaKYC(param) {
    let { personaKycStatus, stripeKycStatus } = param;
    const hasPersonaFailed = personaKycStatus === KYBProgressStatusEnum.Declined || personaKycStatus === KYBProgressStatusEnum.Errored;
    const neverStartedStripeKYC = stripeKycStatus === KycCompletionStatus.InitialIncomplete;
    const isPendingMigrationFromStripeToPersona = (stripeKycStatus === KycCompletionStatus.Completed || stripeKycStatus === KycCompletionStatus.Incomplete || stripeKycStatus === KycCompletionStatus.CompletedWithFutureRequirements) && personaKycStatus != null && [
        KYBProgressStatusEnum.NotStarted,
        KYBProgressStatusEnum.DetailsNeeded
    ].includes(personaKycStatus);
    return hasPersonaFailed || neverStartedStripeKYC || isPendingMigrationFromStripeToPersona;
}
export function useStartKYC() {
    const { personaClient, browser: { openNewTab } } = useIoContext();
    const { api } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { getState } = useStoreStatic();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { KYB_UPDATED_FLOW } = useFeatureFlags();
    const isVendor = useStore(selectors.user.isVendor);
    const { refresh: refreshPersonaKYBStatus } = usePersonaKYBStatus({
        enabled: isVendor
    });
    async function startKYCPersona(businessId) {
        startLoader(personaInquiryLoader);
        const inquiryRequest = KYB_UPDATED_FLOW ? api.vendor.getKYBUpdatedFlowInquiry(businessId) : api.vendor.getKYBInquiryWithSelfie(businessId);
        const result = await handlePromise(inquiryRequest);
        if (result.success) {
            const { inquiryId, sessionToken, inquiryStatus } = result.data;
            if (InquiryStatusEnum.NeedsReview === inquiryStatus) {
                await refreshPersonaKYBStatus({
                    throwOnError: false
                });
                stopLoader(personaInquiryLoader);
                return;
            }
            const client = personaClient({
                onError: ()=>stopLoader(personaInquiryLoader),
                onReady: ()=>stopLoader(personaInquiryLoader),
                onComplete: ()=>refreshPersonaKYBStatus({
                        throwOnError: false
                    })
            });
            client.open(inquiryId, sessionToken);
        } else {
            showErrorToast(commonTexts.toasts.failedToStartKybProcess());
            stopLoader(personaInquiryLoader);
        }
    }
    async function startKYCStripe(businessId) {
        const url = await api.vendor.getStripeHostedKYBUrl(businessId);
        openNewTab(url);
    }
    async function startKYC() {
        const worksAt = selectors.user.business(getState());
        const businessId = worksAt === null || worksAt === void 0 ? void 0 : worksAt.id;
        const stripeKycStatus = worksAt === null || worksAt === void 0 ? void 0 : worksAt.kycCompletionStatus;
        if (businessId == null) {
            return;
        }
        const { data: personaKyb } = await refreshPersonaKYBStatus({
            throwOnError: false
        });
        if (shouldStartPersonaKYC({
            personaKycStatus: personaKyb === null || personaKyb === void 0 ? void 0 : personaKyb.status,
            stripeKycStatus
        })) {
            startKYCPersona(businessId);
        } else {
            startKYCStripe(businessId);
        }
    }
    return {
        startKYC
    };
}
